import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { allProducts } from 'shared/api/masters'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { ProductType } from './types'
import { ConfirmDialog } from 'shared/ui'
import { Table } from 'shared/ui/Table/Table'
import { useSortableProductColumns } from '../resourceList/utility'

export function ProductSelectorDialog({
  onClose,
  onCommit,
  title,
  cta = 'Select',
  shouldHide,
  filteredIds = [],
}: {
  onCommit(pids: number[]): void
  onClose(): void
  title?: string
  cta?: string
  shouldHide?: boolean
  filteredIds?: number[]
}) {
  const products = useAtomValue(allProducts)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const filtered = products.filter(({ id }) => !filteredIds.includes(id))

  const onConfirm = () => {
    onCommit(selectedIds)
  }
  const onCancel = () => {
    setSelectedIds([])
    onClose()
  }
  return (
    <ConfirmDialog
      title={title ?? ''}
      onConfirm={onConfirm}
      onClose={onCancel}
      open={!shouldHide}
      canConfirm={!!selectedIds.length}
      cta={cta}
      cancel="Cancel"
    >
      <ProductSelectorTable
        onSelectedIdChange={setSelectedIds}
        currentSelectedIds={selectedIds}
        allProducts={filtered}
      />
    </ConfirmDialog>
  )
}

function ProductSelectorTable({
  allProducts,
  onSelectedIdChange,
  currentSelectedIds,
}: {
  allProducts: ProductType[]
  currentSelectedIds: number[]
  onSelectedIdChange: Dispatch<SetStateAction<number[]>>
}) {
  const { sorted, columns } = useSortableProductColumns(allProducts)

  const selectedIndexes = useMemo(() => {
    return sorted.reduce<number[]>((acc, { id }, index) => {
      if (currentSelectedIds.includes(id)) {
        return [...acc, index]
      }
      return acc
    }, [])
  }, [sorted, currentSelectedIds])

  const onRowSelect = (index: number) => {
    onSelectedIdChange((prev) => {
      if (index < 0) {
        if (prev.length < sorted.length) return allProducts.map(({ id }) => id)
        return []
      }
      const selectedId = sorted[index].id
      if (prev.includes(selectedId)) {
        return prev.filter((id) => id !== selectedId)
      }
      return [...prev, selectedId]
    })
  }

  return (
    <Table
      columns={columns}
      items={sorted}
      onRowSelect={onRowSelect}
      selectedRows={selectedIndexes}
    />
  )
}
